/**
 * Shared sign-up form used by both the standalone /sign-up page and
 * the onboarding flow (step 3).  It owns all form state, local validation,
 * and the common UI (OAuth buttons, fields, agreement notice).  The parent
 * is responsible for the actual API calls and passes results back via props.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ErrorAlert } from '@/components/Alert';
import Button from '@/components/Button';
import Input from '@/components/Input';
import LoadingAnimation from '@/components/LoadingAnimation';
import AgreementNotice from '@/components/LoginPage/AgreementNotice';
import Divider from '@/components/LoginPage/OrContinueWithDivider';
import SocialLoginButtons from '@/components/LoginPage/SocialLoginButtons';
import { validateComplexPassword, validateEmail } from '@/helpers/validate';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface SignUpFormValues {
  firstName: string;
  lastName: string;
  email: string;
  orgName: string;
  password: string;
  confirmPassword: string;
}

export interface SignUpFormProps {
  // ── Pre-fill ──────────────────────────────────────────────────────────────
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  initialOrgName?: string;

  // ── Field options ─────────────────────────────────────────────────────────
  /** Lock the email field (invite flow: email is pre-determined). */
  emailDisabled?: boolean;
  /** Hide the org-name field (invite flow: org is determined server-side). */
  showOrgField?: boolean;

  // ── Loading overlay ───────────────────────────────────────────────────────
  /**
   * When set, replaces the form body with a spinner and this label.
   * Use to communicate multi-step async progress to the user.
   */
  loadingLabel?: string;

  // ── Errors from the parent (API layer) ───────────────────────────────────
  apiError?: string | null;
  /** Per-field errors returned by the server (keyed by field name). */
  fieldErrors?: Partial<Record<string, string>>;

  // ── Submission ────────────────────────────────────────────────────────────
  onSubmit: (values: SignUpFormValues) => void;
  isSubmitting?: boolean;

  // ── OAuth ─────────────────────────────────────────────────────────────────
  /** Called synchronously before the OAuth provider redirect fires. */
  onBeforeOAuthRedirect?: () => void;

  // ── Optional extras ───────────────────────────────────────────────────────
  /**
   * If provided, shows a compact profile summary pill above the form.
   * Used by the onboarding flow to confirm which profile will be created.
   */
  profileSummary?: { name: string; subtitle?: string; imageUrl?: string };

  /** Renders a back-navigation link below the card. */
  onBack?: () => void;
  backLabel?: string;

  /**
   * Renders a "Sign in" link below the card.
   * Pass `undefined` to omit (onboarding forces new accounts).
   */
  loginLink?: { to: string; label: string; state?: unknown };

  /** Text shown on the divider between OAuth buttons and email form. */
  dividerText?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SignUpForm({
  initialFirstName = '',
  initialLastName = '',
  initialEmail = '',
  initialOrgName = '',
  emailDisabled = false,
  showOrgField = true,
  loadingLabel,
  apiError,
  fieldErrors: externalFieldErrors,
  onSubmit,
  isSubmitting = false,
  onBeforeOAuthRedirect,
  profileSummary,
  onBack,
  backLabel = '← Back',
  loginLink,
  dividerText = 'Or create with email',
}: SignUpFormProps) {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [email, setEmail] = useState(initialEmail);
  const [orgName, setOrgName] = useState(initialOrgName);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localFieldErrors, setLocalFieldErrors] = useState<
    Partial<Record<string, string>>
  >({});

  // Merge local validation errors with any server-side field errors the parent passes
  const fieldErrors = { ...localFieldErrors, ...externalFieldErrors };

  const isLoading = !!loadingLabel;

  // Auto-suggest org name from first + last name when org field is blank
  function suggestOrgName() {
    if (showOrgField && orgName === '') {
      setOrgName([firstName, lastName].filter(Boolean).join(' '));
    }
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};

    if (!firstName.trim()) errs.firstName = 'First name is required.';
    if (!lastName.trim()) errs.lastName = 'Last name is required.';
    if (!emailDisabled && !validateEmail(email)) {
      errs.email = 'Please enter a valid email address.';
    }
    if (showOrgField && !orgName.trim()) {
      errs.orgName = 'Organisation name is required.';
    }
    if (!validateComplexPassword(password)) {
      errs.password =
        'Password must be at least 8 characters with a number, uppercase, and lowercase letter.';
    }
    if (password !== confirmPassword) {
      errs.confirmPassword = 'Passwords do not match.';
    }

    setLocalFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    // Auto-suggest org before validating so the user doesn't see a blank error
    if (showOrgField && orgName === '') {
      const suggested = [firstName, lastName].filter(Boolean).join(' ');
      setOrgName(suggested);
      // Use the freshly computed value directly (state update is async)
      const errs: Record<string, string> = {};
      if (!suggested.trim()) errs.orgName = 'Organisation name is required.';
      if (!firstName.trim()) errs.firstName = 'First name is required.';
      if (!lastName.trim()) errs.lastName = 'Last name is required.';
      if (!emailDisabled && !validateEmail(email))
        errs.email = 'Please enter a valid email address.';
      if (!validateComplexPassword(password))
        errs.password =
          'Password must be at least 8 characters with a number, uppercase, and lowercase letter.';
      if (password !== confirmPassword)
        errs.confirmPassword = 'Passwords do not match.';
      setLocalFieldErrors(errs);
      if (Object.keys(errs).length > 0) return;
      onSubmit({
        firstName,
        lastName,
        email,
        orgName: suggested,
        password,
        confirmPassword,
      });
      return;
    }

    if (!validate()) return;
    onSubmit({
      firstName,
      lastName,
      email,
      orgName,
      password,
      confirmPassword,
    });
  }

  return (
    <div className="sm:mx-auto sm:w-full sm:max-w-md lg:max-w-lg">
      <div className="relative bg-white py-2 px-4 sm:rounded-lg sm:px-10 sm:py-8 lg:border lg:border-gray-100">
        {/* ── Profile summary pill (onboarding) ──────────────────────────── */}
        {profileSummary && (
          <div className="mb-6 flex items-center gap-3 rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
            {profileSummary.imageUrl ? (
              <img
                src={profileSummary.imageUrl}
                alt={profileSummary.name}
                className="h-8 w-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-brand-600">
                  {profileSummary.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {profileSummary.name}
              </p>
              {profileSummary.subtitle && (
                <p className="text-xs text-gray-500 truncate">
                  {profileSummary.subtitle}
                </p>
              )}
            </div>
            <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex-shrink-0">
              Profile ready
            </span>
          </div>
        )}

        {/* ── API / generic error ─────────────────────────────────────────── */}
        {apiError && <ErrorAlert message={apiError} className="mb-4" />}

        {/* ── Loading overlay ───────────────────────────────────────────── */}
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-lg bg-white/90 backdrop-blur-sm">
            <LoadingAnimation className="h-16 w-16" />
            <p className="text-sm font-medium text-gray-700">{loadingLabel}</p>
          </div>
        )}

        {/* ── Form body ─────────────────────────────────────────────────── */}
        <div className="space-y-4">
          {/* OAuth */}
          <SocialLoginButtons onBeforeRedirect={onBeforeOAuthRedirect} />
          <Divider text={dividerText} />

          {/* Name row */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              id="signup-first-name"
              label="First name"
              placeholder="Jane"
              type="text"
              value={firstName}
              onChange={setFirstName}
              error={!!fieldErrors.firstName}
              message={fieldErrors.firstName}
            />
            <Input
              id="signup-last-name"
              label="Last name"
              placeholder="Smith"
              type="text"
              value={lastName}
              onChange={setLastName}
              error={!!fieldErrors.lastName}
              message={fieldErrors.lastName}
            />
          </div>

          {/* Email */}
          <Input
            id="signup-email"
            label="Work email"
            placeholder="jane@company.com"
            type="email"
            value={email}
            onChange={setEmail}
            onFocus={suggestOrgName}
            disabled={emailDisabled}
            error={!!fieldErrors.email}
            message={fieldErrors.email}
          />

          {/* Organisation */}
          {showOrgField && (
            <div>
              <Input
                id="signup-org-name"
                label="Organisation name"
                placeholder="Acme Corp"
                type="text"
                value={orgName}
                onChange={setOrgName}
                onFocus={suggestOrgName}
                error={!!fieldErrors.orgName}
                message={fieldErrors.orgName}
              />
              <p className="mt-1 text-xs text-gray-500">
                This is the name of your company or team workspace.
              </p>
            </div>
          )}

          {/* Password */}
          <div>
            <Input
              id="signup-password"
              label="Password"
              placeholder="Create a password"
              type="password"
              value={password}
              onChange={setPassword}
              error={!!fieldErrors.password}
              message={fieldErrors.password}
            />
            {!fieldErrors.password && (
              <p className="mt-1 text-xs text-gray-500">
                8+ characters with uppercase, lowercase, and a number.
              </p>
            )}
          </div>

          {/* Confirm password */}
          <Input
            id="signup-confirm-password"
            label="Confirm password"
            placeholder="Repeat your password"
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            error={!!fieldErrors.confirmPassword}
            message={fieldErrors.confirmPassword}
          />

          {/* Submit */}
          <Button
            buttonText="Create account"
            kind="primary"
            fullWidth
            loading={isSubmitting}
            onClick={handleSubmit}
          />

          {/* T&C */}
          <div className="flex justify-center pt-1">
            <AgreementNotice mode="signup" />
          </div>
        </div>
      </div>

      {/* ── Below-card actions ─────────────────────────────────────────────── */}
      {!isLoading && (onBack || loginLink) && (
        <div className="mt-4 flex flex-col items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              {backLabel}
            </button>
          )}
          {loginLink && (
            <Link
              to={loginLink.to}
              state={loginLink.state}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              {loginLink.label}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
